import { useRouter } from "lib/router";
import { useEffect, useState } from "react";
import type { PageScrollValues } from "tracking";

export const useScrollPercentage = (
  trackScrollDepths?: PageScrollValues[],
  callback?: (params: { scrollY: PageScrollValues }) => void
): { scrollY: PageScrollValues } => {
  const { asPath } = useRouter();
  const [state, setState] = useState<{
    scrollDepths: PageScrollValues[] | undefined;
    scrollY: PageScrollValues;
  }>({
    scrollDepths: trackScrollDepths,
    scrollY: 0
  });

  const { scrollDepths, scrollY } = state;

  // reset scrolltracker params on path change
  useEffect(() => {
    setState(() => ({
      scrollDepths: trackScrollDepths,
      scrollY: 0 as PageScrollValues
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPath]);

  useEffect(() => {
    const endScrollTracker = () =>
      window.removeEventListener("scroll", handleScroll);

    const handleScroll = () => {
      const h = document.documentElement;
      const b = document.body;

      const scrollTop = h.scrollTop || b.scrollTop;
      const scrollHeight = h.scrollHeight || b.scrollHeight;
      const { clientHeight } = h;

      const scrollPercent = (scrollTop / (scrollHeight - clientHeight)) * 100;

      if (scrollDepths && scrollPercent) {
        const nextMinDepth = Math.min(...scrollDepths);

        if (scrollPercent >= nextMinDepth) {
          const updatedScrollDepths = scrollDepths.filter(
            (depth) => depth !== nextMinDepth
          );

          if (updatedScrollDepths.length === 0) endScrollTracker();

          if (callback) {
            callback({
              scrollY: nextMinDepth as PageScrollValues
            });
          }
          setState({
            scrollY: nextMinDepth as PageScrollValues,
            scrollDepths: updatedScrollDepths
          });
        }
      }
    };

    window.addEventListener("scroll", handleScroll);

    return endScrollTracker;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollDepths, scrollY, state]);

  return { scrollY };
};
